import {useEffect, useMemo, useState} from "react";
import {Alert, Badge, Button, Col, Form, Modal, Nav, Row, Spinner, Tab} from "react-bootstrap";
import JobImageGallary from "../JobImageGallary";
import JobImageUpload from "../JobImageUpload";
import {getAllProperties, getPropertyById} from "../../service/PropertyService";
import {createJob, getJobDetails, updateJobAdmin} from "../../service/JobService";
import {getJobPricing, updateJobPricing} from "../../service/PricingService";
import {DEFAULT_SCHEDULER_WORKER_ID} from "./boardConfigs";
import PropertySearchSelect, {getPropertyId, getPropertyLabel, normalizePropertySearchText} from "./PropertySearchSelect";
import {
    buildTaskAdminPayload,
    buildTaskCreatePayload,
    buildTaskFormFromJob,
    buildTaskFormFromProperty,
    buildTaskPricingPayload,
    createEmptyTaskForm,
    sortProperties,
    TASK_STATUS_OPTIONS,
    TASK_TIME_OPTIONS,
} from "./taskFormUtils";

async function getResponseError(response) {
    const responseText = await response.text();
    return responseText || "Request failed.";
}

function mergeTaskFormWithPricing(taskDetails, pricingDetails) {
    return {
        ...buildTaskFormFromJob(taskDetails),
        agent_price: pricingDetails?.total_override_price ?? "",
        worker_price: pricingDetails?.labour_override_price ?? "",
        comments: pricingDetails?.comments ?? "",
    };
}

function populateMissingTaskKeyNumber(taskForm, properties = []) {
    if (taskForm?.key_number) {
        return taskForm;
    }

    const matchingProperty = properties.find((property) => {
        return getPropertyId(property) === String(taskForm?.property_id || "");
    });

    if (!matchingProperty?.key_number) {
        return taskForm;
    }

    return {
        ...taskForm,
        key_number: matchingProperty.key_number,
    };
}

export default function TaskModal({
    fieldWorkerArray,
    initialDate,
    onClose,
    onSaved,
    show,
    taskId,
}) {
    const isEditMode = Boolean(taskId);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [properties, setProperties] = useState([]);
    const [images, setImages] = useState([]);
    const [activeTab, setActiveTab] = useState("details");
    const [propertySearchText, setPropertySearchText] = useState("");
    const [taskForm, setTaskForm] = useState(() => createEmptyTaskForm(initialDate, DEFAULT_SCHEDULER_WORKER_ID));
    const [originalWorkerId, setOriginalWorkerId] = useState("");

    const availableWorkers = useMemo(() => {
        return fieldWorkerArray.filter((worker) => worker.isDeleted !== true);
    }, [fieldWorkerArray]);

    const selectedProperty = useMemo(() => {
        if (isEditMode || !taskForm.property_id) {
            return null;
        }

        return properties.find((property) => getPropertyId(property) === String(taskForm.property_id)) ?? null;
    }, [isEditMode, properties, taskForm.property_id]);

    useEffect(() => {
        if (show || isEditMode) {
            return;
        }

        setPropertySearchText("");
        setTaskForm(createEmptyTaskForm(initialDate, DEFAULT_SCHEDULER_WORKER_ID));
    }, [initialDate, isEditMode, show]);

    useEffect(() => {
        if (!show) {
            setActiveTab("details");
            setImages([]);
            setPropertySearchText("");
            setOriginalWorkerId("");
        }
    }, [show]);

    useEffect(() => {
        if (isEditMode) {
            return;
        }

        setPropertySearchText(selectedProperty ? getPropertyLabel(selectedProperty) : "");
    }, [isEditMode, selectedProperty]);

    useEffect(() => {
        if (!show) {
            return;
        }

        let isMounted = true;

        async function loadModalData() {
            setIsLoading(true);
            setErrorMessage("");

            try {
                if (isEditMode) {
                    const [taskResponse, pricingResponse] = await Promise.all([
                        getJobDetails(taskId),
                        getJobPricing(taskId),
                    ]);

                    if (taskResponse.status !== 200) {
                        if (isMounted) {
                            setErrorMessage(await getResponseError(taskResponse));
                        }
                        return;
                    }

                    if (pricingResponse.status !== 200) {
                        if (isMounted) {
                            setErrorMessage(await getResponseError(pricingResponse));
                        }
                        return;
                    }

                    const [taskDetails, pricingDetails] = await Promise.all([
                        taskResponse.json(),
                        pricingResponse.json(),
                    ]);
                    let propertyDetails = null;

                    if (taskDetails?.property_id) {
                        const propertyResponse = await getPropertyById(taskDetails.property_id);
                        if (propertyResponse.status === 200) {
                            propertyDetails = await propertyResponse.json();
                        } else if (propertyResponse.status !== 404) {
                            if (isMounted) {
                                setErrorMessage(await getResponseError(propertyResponse));
                            }
                            return;
                        }
                    }

                    if (isMounted) {
                        setOriginalWorkerId(taskDetails?.cos_job_worker || "");
                        setTaskForm(populateMissingTaskKeyNumber(
                            mergeTaskFormWithPricing(taskDetails, pricingDetails),
                            propertyDetails ? [propertyDetails] : [],
                        ));
                    }
                    return;
                }

                const propertiesResponse = await getAllProperties();
                if (propertiesResponse.status !== 200) {
                    if (isMounted) {
                        setErrorMessage(await getResponseError(propertiesResponse));
                    }
                    return;
                }

                const propertyResult = await propertiesResponse.json();
                if (!isMounted) {
                    return;
                }

                setProperties(sortProperties(propertyResult));
                setOriginalWorkerId(DEFAULT_SCHEDULER_WORKER_ID);
                setTaskForm(populateMissingTaskKeyNumber(
                    createEmptyTaskForm(initialDate, DEFAULT_SCHEDULER_WORKER_ID),
                    propertyResult,
                ));
            } catch (error) {
                console.log("error", error);
                if (isMounted) {
                    setErrorMessage("Unable to load task details.");
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadModalData();

        return () => {
            isMounted = false;
        };
    }, [initialDate, isEditMode, show, taskId]);

    function handlePropertyChange(property) {
        const propertyId = getPropertyId(property);
        const nextProperty = properties.find((option) => getPropertyId(option) === String(propertyId));
        if (!nextProperty) {
            setPropertySearchText("");
            setTaskForm(createEmptyTaskForm(initialDate, DEFAULT_SCHEDULER_WORKER_ID));
            return;
        }

        setPropertySearchText(getPropertyLabel(nextProperty));
        setTaskForm(buildTaskFormFromProperty(nextProperty, initialDate, DEFAULT_SCHEDULER_WORKER_ID));
    }

    function handlePropertySearchTextChange(nextSearchText) {
        setPropertySearchText(nextSearchText);

        if (!selectedProperty) {
            return;
        }

        const normalizedNextSearch = normalizePropertySearchText(nextSearchText);
        const normalizedSelectedLabel = normalizePropertySearchText(getPropertyLabel(selectedProperty));

        if (normalizedNextSearch === normalizedSelectedLabel) {
            return;
        }

        setTaskForm((currentTaskForm) => ({
            ...currentTaskForm,
            property_id: "",
            full_address: "",
            key_number: "",
            cos_customer_name: "",
            agent_name: "",
            phone: "",
            email: "",
            check_out: "",
            check_in: "",
            consumable_type: null,
            bedroom: 0,
            bathroom: 0,
            sofabed: 0,
            king: 0,
            queen: 0,
            single: 0,
        }));
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setIsSaving(true);
        setErrorMessage("");

        try {
            if (!isEditMode && !taskForm.property_id) {
                setErrorMessage("Please select a property.");
                return;
            }

            if (isEditMode) {
                const nextTaskForm = originalWorkerId !== String(taskForm.cos_job_worker || "")
                    ? {
                        ...taskForm,
                        date: initialDate,
                    }
                    : taskForm;

                const updateResponse = await updateJobAdmin(buildTaskAdminPayload(nextTaskForm));
                if (updateResponse.status !== 200) {
                    setErrorMessage(await getResponseError(updateResponse));
                    return;
                }

                const updatedTask = await updateResponse.json();
                const pricingResponse = await updateJobPricing(buildTaskPricingPayload(nextTaskForm));
                if (pricingResponse.status !== 200) {
                    setErrorMessage(`Task saved, but pricing failed to save: ${await getResponseError(pricingResponse)}`);
                    return;
                }

                onSaved(updatedTask);
                return;
            }

            const createResponse = await createJob(buildTaskCreatePayload(taskForm));
            if (createResponse.status !== 201) {
                setErrorMessage(await getResponseError(createResponse));
                return;
            }

            const createdTask = await createResponse.json();
            const updateResponse = await updateJobAdmin(buildTaskAdminPayload({
                ...taskForm,
                cos_job_id: createdTask.cos_job_id,
                cos_job_worker: taskForm.cos_job_worker || createdTask.cos_job_worker,
            }));

            if (updateResponse.status !== 200) {
                setErrorMessage(await getResponseError(updateResponse));
                return;
            }

            const updatedTask = await updateResponse.json();
            const pricingResponse = await updateJobPricing(buildTaskPricingPayload({
                ...taskForm,
                cos_job_id: createdTask.cos_job_id,
            }));
            if (pricingResponse.status !== 200) {
                setErrorMessage(`Task saved, but pricing failed to save: ${await getResponseError(pricingResponse)}`);
                return;
            }

            onSaved(updatedTask);
        } catch (error) {
            console.log("error", error);
            setErrorMessage("Unable to save task.");
        } finally {
            setIsSaving(false);
        }
    }

    const taskDetailsContent = (
        <Row className="g-3">
            <Col sm={12}>
                <Form.Label>Property</Form.Label>
                {isEditMode ? (
                    <Form.Control value={taskForm.full_address} readOnly />
                ) : (
                    <PropertySearchSelect
                        options={properties}
                        required={!taskForm.property_id}
                        searchText={propertySearchText}
                        selectedProperty={selectedProperty}
                        onSearchTextChange={handlePropertySearchTextChange}
                        onSelectProperty={handlePropertyChange}
                    />
                )}
            </Col>
            <Col sm={12}>
                <Form.Label>Address</Form.Label>
                <Form.Control value={taskForm.full_address} readOnly />
            </Col>
            <Col sm={6}>
                <Form.Label>Key Number</Form.Label>
                <Form.Control
                    value={taskForm.key_number}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        key_number: event.target.value,
                    })}
                />
            </Col>
            <Col sm={12}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={5}
                    required
                    value={taskForm.description}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        description: event.target.value,
                    })}
                />
            </Col>
            <Col sm={4}>
                <Form.Label>Task Date</Form.Label>
                <Form.Control
                    required
                    type="date"
                    value={taskForm.date}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        date: event.target.value,
                    })}
                />
            </Col>
            <Col sm={4}>
                <Form.Label>Worker</Form.Label>
                <Form.Select
                    value={taskForm.cos_job_worker}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        cos_job_worker: event.target.value,
                        date: initialDate,
                    })}
                >
                    {availableWorkers.map((worker) => (
                        <option key={worker.id} value={worker.id}>{worker.name}</option>
                    ))}
                </Form.Select>
            </Col>
            <Col sm={4}>
                <Form.Label>Status</Form.Label>
                <Form.Select
                    value={taskForm.status}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        status: event.target.value,
                    })}
                >
                    {TASK_STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>{status}</option>
                    ))}
                </Form.Select>
            </Col>
            <Col sm={6}>
                <Form.Label>Phone</Form.Label>
                <Form.Control
                    value={taskForm.phone}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        phone: event.target.value,
                    })}
                />
            </Col>
            <Col sm={6}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                    value={taskForm.email}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        email: event.target.value,
                    })}
                />
            </Col>
            <Col sm={6}>
                <Form.Label>Check Out</Form.Label>
                <Form.Select
                    value={taskForm.check_out}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        check_out: event.target.value,
                    })}
                >
                    {TASK_TIME_OPTIONS.map((timeOption) => (
                        <option key={timeOption || "empty-check-out"} value={timeOption}>
                            {timeOption}
                        </option>
                    ))}
                </Form.Select>
            </Col>
            <Col sm={6}>
                <Form.Label>Check In</Form.Label>
                <Form.Select
                    value={taskForm.check_in}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        check_in: event.target.value,
                    })}
                >
                    {TASK_TIME_OPTIONS.map((timeOption) => (
                        <option key={timeOption || "empty-check-in"} value={timeOption}>
                            {timeOption}
                        </option>
                    ))}
                </Form.Select>
            </Col>
            <Col sm={4}>
                <Form.Label>Agent Price</Form.Label>
                <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    aria-label="Agent Price"
                    value={taskForm.agent_price}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        agent_price: event.target.value,
                    })}
                />
            </Col>
            <Col sm={4}>
                <Form.Label>Worker Price</Form.Label>
                <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    aria-label="Worker Price"
                    value={taskForm.worker_price}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        worker_price: event.target.value,
                    })}
                />
            </Col>
            <Col sm={4}>
                <Form.Label>Comments</Form.Label>
                <Form.Control
                    value={taskForm.comments}
                    onChange={(event) => setTaskForm({
                        ...taskForm,
                        comments: event.target.value,
                    })}
                />
            </Col>
        </Row>
    );

    return (
        <Modal show={show} onHide={onClose} size="lg" backdrop="static">
            <Modal.Header closeButton={!isSaving}>
                <Modal.Title>{isEditMode ? "Edit Task" : "Add Task"}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
                    {isLoading ? (
                        <div style={{display: "flex", justifyContent: "center", padding: "32px 0"}}>
                            <Spinner animation="border" role="status" />
                        </div>
                    ) : (
                        isEditMode ? (
                            <Tab.Container activeKey={activeTab} onSelect={(nextTab) => setActiveTab(nextTab || "details")}>
                                <>
                                    <Nav variant="tabs" className="mb-3">
                                        <Nav.Item>
                                            <Nav.Link eventKey="details">Task Details</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="images">
                                                Images <Badge bg="secondary">{images.length}</Badge>
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                    <Tab.Content>
                                        <Tab.Pane eventKey="details">
                                            {taskDetailsContent}
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="images">
                                            <Row className="g-3">
                                                <Col sm={12}>
                                                    <JobImageUpload
                                                        job={taskForm}
                                                        images={images}
                                                        setImages={setImages}
                                                    />
                                                </Col>
                                                <Col sm={12}>
                                                    <JobImageGallary
                                                        job={taskForm}
                                                        isReadOnly={false}
                                                        images={images}
                                                        setImages={setImages}
                                                    />
                                                </Col>
                                            </Row>
                                        </Tab.Pane>
                                    </Tab.Content>
                                </>
                            </Tab.Container>
                        ) : taskDetailsContent
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    {activeTab === "details" ? (
                        <Button type="submit" variant="primary" disabled={isLoading || isSaving}>
                            {isSaving ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Saving...
                                </>
                            ) : "Save Task"}
                        </Button>
                    ) : null}
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
